import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errMsg, money } from "@/lib/api";

const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();
const signalerRetenus = (retenus, cible) => {
  if (retenus?.length) toast.info(`Nom d'annonce ${retenus.map((a) => `« ${a} »`).join(", ")} retenu pour ${cible} : les prochains imports iront directement au bon endroit (modifiable dans Paramètres › Logements).`, { duration: 8000 });
};

const Ligne = ({ r, cibles, coche, onCoche, onChoisi, prefix }) => {
  const [envoi, setEnvoi] = useState("");
  const choisir = async (logement) => {
    setEnvoi(logement);
    try { const { data } = await api.put(`/reservations/${r.id}/logement`, { logement }); toast.success(`${r.code} → ${logement}`); signalerRetenus(data.retenus, logement); onChoisi([r.id]); }
    catch (e) { toast.error(errMsg(e)); setEnvoi(""); }
  };
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-2 py-2 border-t border-amber-100" data-testid={`${prefix}-resa-${r.code}`}>
      <div className="flex flex-1 min-w-0 items-start gap-2 text-sm">
        <Checkbox checked={coche} onCheckedChange={(v) => onCoche(r.id, !!v)} className="mt-0.5 bg-white" data-testid={`${prefix}-resa-${r.code}-check`} />
        <div className="min-w-0">
          <span className="font-mono font-semibold">{r.code}</span>
          <span className="text-slate-500"> · {r.plateforme} · {r.client || "—"} · {r.debut} → {r.fin} · {money(r.montant)}</span>
          {r.annulee && <span className="ml-1 text-xs text-red-600">(annulée)</span>}
          <span className="block text-xs text-amber-800">Actuellement : {r.logement || "aucun logement"}{r.suggestion && ` — suggestion : ${r.suggestion} (l'autre appartement est déjà occupé à ces dates)`}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pl-6 lg:pl-0">
        {cibles.map((c) => (
          <Button key={c} size="sm" variant={c === r.suggestion ? "default" : "outline"} disabled={!!envoi} onClick={() => choisir(c)}
            className={c === r.suggestion ? "bg-emerald-700 hover:bg-emerald-800" : "bg-white"} data-testid={`${prefix}-resa-${r.code}-${slug(c)}`}>
            {envoi === c && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}{c}
          </Button>
        ))}
      </div>
    </div>
  );
};

const ConfirmLot = ({ ouvert, n, cible, envoi, onAnnuler, onConfirmer, prefix }) => (
  <AlertDialog open={ouvert} onOpenChange={(o) => !o && onAnnuler()}>
    <AlertDialogContent data-testid={`${prefix}-lot-dialog`}>
      <AlertDialogHeader>
        <AlertDialogTitle>Déplacer {n} réservation(s) ?</AlertDialogTitle>
        <AlertDialogDescription data-testid={`${prefix}-lot-recap`}>
          Les {n} réservation(s) cochée(s) seront rattachées à « {cible} ». Le dépôt et la facture de commission suivent.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${prefix}-lot-annuler`}>Annuler</AlertDialogCancel>
        <AlertDialogAction className="bg-emerald-700 hover:bg-emerald-800" disabled={envoi} data-testid={`${prefix}-lot-confirmer`}
          onClick={(e) => { e.preventDefault(); onConfirmer(); }}>
          {envoi && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirmer
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const BarreLot = ({ liste, coches, setCoches, cibles, onFait, prefix }) => {
  const [cible, setCible] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const tous = liste.length > 0 && liste.every((r) => coches.has(r.id));
  const confirmer = async () => {
    setEnvoi(true);
    try {
      const ids = [...coches];
      const { data } = await api.post("/reservations/logement/lot", { ids, logement: cible });
      toast.success(`${data.trouvees} réservation(s) → ${cible}`);
      signalerRetenus(data.retenus, cible);
      setOuvert(false); onFait(ids);
    } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-t border-amber-200" data-testid={`${prefix}-lot-barre`}>
      <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
        <Checkbox checked={tous} onCheckedChange={(v) => setCoches(new Set(v ? liste.map((r) => r.id) : []))} className="bg-white" data-testid={`${prefix}-tout-cocher`} />
        Tout cocher
      </label>
      <span className="text-xs text-amber-800" data-testid={`${prefix}-nb-coches`}>{coches.size} cochée(s)</span>
      <Select value={cible} onValueChange={setCible}>
        <SelectTrigger className="h-8 w-56 bg-white text-xs" data-testid={`${prefix}-lot-cible`}><SelectValue placeholder="Choisir l'appartement" /></SelectTrigger>
        <SelectContent>{cibles.map((c) => <SelectItem key={c} value={c} data-testid={`${prefix}-lot-cible-${slug(c)}`}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" disabled={!coches.size || !cible} onClick={() => setOuvert(true)} data-testid={`${prefix}-lot-appliquer`}>
        Déplacer la sélection
      </Button>
      <ConfirmLot ouvert={ouvert} n={coches.size} cible={cible} envoi={envoi} onAnnuler={() => setOuvert(false)} onConfirmer={confirmer} prefix={prefix} />
    </div>
  );
};

export const LogementsAVerifier = ({ onChange, importIds, prefix = "verif" }) => {
  const [data, setData] = useState(null);
  const [coches, setCoches] = useState(new Set());
  const filtre = importIds?.join(",") || "";
  const charger = useCallback(() => {
    api.get("/logements/a-verifier", { params: filtre ? { import_ids: filtre } : {} }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [filtre]);
  useEffect(() => { setCoches(new Set()); charger(); }, [charger]);
  if (importIds && !importIds.length) return null;
  if (!data || !data.reservations.length || !data.cibles.length) return null;
  const retirer = (ids) => {
    setData({ ...data, reservations: data.reservations.filter((x) => !ids.includes(x.id)) });
    setCoches(new Set([...coches].filter((i) => !ids.includes(i))));
    onChange?.();
  };
  const cocher = (id, v) => { const s = new Set(coches); if (v) s.add(id); else s.delete(id); setCoches(s); };
  return (
    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid={importIds ? "import-alerte-logements" : "logements-a-verifier"}>
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-amber-900" data-testid={importIds ? `${prefix}-titre` : "logements-a-verifier-titre"}>
            {importIds ? "Import terminé · " : ""}Logement à vérifier · {data.reservations.length} réservation(s)
          </h3>
          <p className="text-xs text-amber-800">
            {importIds ? "Ces réservations viennent d'arriver sur un logement qui n'est pas un appartement de location courte durée. " : "Ces réservations Airbnb / Booking ne sont sur aucun appartement de location courte durée. "}
            Choisissez le bon d'un clic, ou cochez-en plusieurs pour les déplacer ensemble (le dépôt et la facture de commission suivent).
          </p>
        </div>
      </div>
      <BarreLot liste={data.reservations} coches={coches} setCoches={setCoches} cibles={data.cibles} onFait={retirer} prefix={prefix} />
      <div className="max-h-96 overflow-y-auto pr-1">
        {data.reservations.map((r) => <Ligne key={r.id} r={r} cibles={data.cibles} coche={coches.has(r.id)} onCoche={cocher} onChoisi={retirer} prefix={prefix} />)}
      </div>
    </div>
  );
};
